import { useState, useEffect, useCallback } from 'react';
import { getReadingLogs, createReadingLog, deleteReadingLog, deleteMultipleReadingLogs, ReadingLog, Book } from '../lib/nocodb';

// Enhanced ReadingLog type with local book data
type EnhancedReadingLog = ReadingLog & {
  _localBookData?: Book;
};

export function useReadingLogs() {
  const [readingLogs, setReadingLogs] = useState<EnhancedReadingLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchReadingLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getReadingLogs();
      
      // Preserve any existing local book data when merging with API data
      setReadingLogs(prevLogs => {
        const apiLogs = response.list || [];
        const localBookData = JSON.parse(localStorage.getItem('readingLogBookData') || '{}');
        
        const preservedLogs = apiLogs.map(apiLog => {
          // First try to find existing local book data in current state
          const existingLog = prevLogs.find(prevLog => prevLog.Id === apiLog.Id);
          if (existingLog && existingLog._localBookData) {
            return {
              ...apiLog,
              _localBookData: existingLog._localBookData
            };
          }
          
          // Then try to restore from localStorage
          if (localBookData[apiLog.Id]) {
            return {
              ...apiLog,
              _localBookData: localBookData[apiLog.Id]
            };
          }
          
          return apiLog;
        });
        return preservedLogs;
      });
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReadingLogs();
  }, [fetchReadingLogs]);

  const addReadingLog = async (book_id: number, notes: string, bookData?: Book) => {
    try {
      const newLog = await createReadingLog({ book_id, notes });
      
      // Add local book data if provided
      const enhancedLog: EnhancedReadingLog = {
        ...newLog,
        _localBookData: bookData
      };
      
      // Store in localStorage for persistence
      if (bookData) {
        const localBookData = JSON.parse(localStorage.getItem('readingLogBookData') || '{}');
        localBookData[newLog.Id] = bookData;
        localStorage.setItem('readingLogBookData', JSON.stringify(localBookData));
      }
      
      // Add to the current list instead of refetching
      setReadingLogs(prevLogs => [enhancedLog, ...prevLogs]);
      return newLog;
    } catch (err) {
      setError(err as Error);
      throw err; // Re-throw to be caught in the component
    }
  };

  const removeReadingLog = async (logId: number) => {
    try {
      await deleteReadingLog(logId);
      
      // Remove from localStorage if it exists
      const localBookData = JSON.parse(localStorage.getItem('readingLogBookData') || '{}');
      if (localBookData[logId]) {
        delete localBookData[logId];
        localStorage.setItem('readingLogBookData', JSON.stringify(localBookData));
      }
      
      // Remove from state
      setReadingLogs(prevLogs => prevLogs.filter(log => log.Id !== logId));
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  const removeMultipleReadingLogs = async (logIds: number[]) => {
    try {
      await deleteMultipleReadingLogs(logIds);
      
      // Remove from localStorage
      const localBookData = JSON.parse(localStorage.getItem('readingLogBookData') || '{}');
      logIds.forEach(id => {
        if (localBookData[id]) {
          delete localBookData[id];
        }
      });
      localStorage.setItem('readingLogBookData', JSON.stringify(localBookData));
      
      // Remove from state
      setReadingLogs(prevLogs => prevLogs.filter(log => !logIds.includes(log.Id)));
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  return { 
    readingLogs, 
    loading, 
    error, 
    refetchReadingLogs: fetchReadingLogs, 
    addReadingLog,
    removeReadingLog,
    removeMultipleReadingLogs
  };
}
